import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { HOUSEHOLD_PLAN_CONFIGS } from '../../constants/household-plans';
import {
  PaymentPlan,
  PlanUiId,
  toPlanUiId,
} from '../../models/payment-plan.model';
import { UserService } from '../../service/user.service';
import {
  buildPlanFeatureLines,
  PlanFeatureLine,
} from '../../utils/plan-feature-lines';

@Component({
  selector: 'app-subscriptions',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ParentButtonComponent, CurrencyPipe],
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.scss',
})
export class SubscriptionsComponent implements OnInit {
  #userService = inject(UserService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);

  public user = input<AuthGen.User>();

  readonly planConfigs = HOUSEHOLD_PLAN_CONFIGS;
  readonly checkoutNotice = signal<'success' | 'cancel' | 'error' | null>(null);
  readonly checkoutLoadingId = signal<PlanUiId | null>(null);
  readonly #lang = signal(
    this.#translate.currentLang || this.#translate.defaultLang,
  );

  readonly plans = computed(() => this.#userService.plans());
  readonly plansLoading = computed(() =>
    this.#userService.plansQuery.isPending(),
  );
  readonly plansError = computed(() => this.#userService.plansQuery.isError());

  readonly currentPlanId = computed<PlanUiId>(() => {
    const fromEntitlement = this.#userService.entitlement()?.planCode;
    if (fromEntitlement) {
      return toPlanUiId(fromEntitlement);
    }
    return toPlanUiId(
      this.user()?.subscription?.type ?? this.user()?.accountType,
    );
  });

  readonly catalogByCode = computed(() => {
    const map = new Map<PlanUiId, PaymentPlan>();
    for (const plan of this.plans()) {
      map.set(toPlanUiId(plan.code), plan);
    }
    return map;
  });

  readonly featuresByPlan = computed(() => {
    this.#lang();
    const map = new Map<PlanUiId, PlanFeatureLine[]>();
    for (const config of this.planConfigs) {
      map.set(config.id, this.#featureLinesFor(config.id));
    }
    return map;
  });

  constructor() {
    this.#translate.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event) => this.#lang.set(event.lang));
  }

  ngOnInit(): void {
    const checkout = this.#route.snapshot.queryParamMap.get('checkout');
    if (checkout === 'success' || checkout === 'cancel') {
      this.checkoutNotice.set(checkout);
      void this.#userService.invalidatePayments();
      void this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { checkout: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  catalogPlan(id: PlanUiId): PaymentPlan | undefined {
    return this.catalogByCode().get(id);
  }

  featureLines(id: PlanUiId): PlanFeatureLine[] {
    return this.featuresByPlan().get(id) ?? [];
  }

  isCurrent(id: PlanUiId): boolean {
    return this.currentPlanId() === id;
  }

  formatPrice(id: PlanUiId): { amount: number; currency: string } | null {
    const plan = this.catalogPlan(id);
    if (!plan || plan.billingKind === 'free' || plan.amountCents <= 0) {
      return null;
    }
    return {
      amount: plan.amountCents / 100,
      currency: (plan.currency || 'EUR').toUpperCase(),
    };
  }

  dismissNotice(): void {
    this.checkoutNotice.set(null);
  }

  async startCheckout(planId: PlanUiId): Promise<void> {
    if (planId === 'base' || this.isCurrent(planId)) {
      return;
    }

    const priceId = this.catalogPlan(planId)?.stripePriceId?.trim();
    if (!priceId) {
      this.checkoutNotice.set('error');
      return;
    }

    this.checkoutNotice.set(null);
    this.checkoutLoadingId.set(planId);
    try {
      const result = await this.#userService.startCheckout.mutateAsync(priceId);
      if (result?.url) {
        window.location.href = result.url;
        return;
      }
      this.checkoutNotice.set('error');
    } catch {
      this.checkoutNotice.set('error');
    } finally {
      this.checkoutLoadingId.set(null);
    }
  }

  #featureLinesFor(id: PlanUiId): PlanFeatureLine[] {
    return buildPlanFeatureLines(this.catalogPlan(id), (key, params) =>
      this.#translate.instant(key, params),
    );
  }
}
